/**
 * Aufgaben — the central all-process decision queue (T5). The all-process
 * sibling of the Eingang screen: same task-backed GET /tasks worklist, but every
 * process (not just mail). List chrome = ListView + DataTable (the InvoicesScreen
 * precedent); saved-view presets are FilterChips; invoice / renewal / commission
 * rows deep-link into their operable screens, everything else opens the peek.
 *
 * STEP 3 (the Herzstück) adds the decision surface on top of the step-2 list:
 *   • AufgabePeek — the GET /tasks/:id split-view (Fakten + Kommunikation +
 *     Anhänge + Historie) + a DecisionBar wired to the existing task actions
 *     (complete / in-progress / cancel / reopen), with Auto-Next on a terminal
 *     decision and J/K/E/Enter/Esc keyboard control.
 *   • Selection + BULK — a checkbox column feeds a bulk action bar → POST
 *     /tasks/bulk (complete/cancel) with per-id result handling.
 *   • "Neue Aufgabe" — a create dialog → POST /tasks, list-invalidated on success.
 *
 * The queue REUSES the existing machinery — GET /tasks (apiClient.tasks.list),
 * the split-view + task actions + bulk, and the shared data/ui primitives — it
 * introduces no new endpoint beyond the step-1 POST /tasks and no schema change
 * (priority is a pure DISPLAY bucket).
 *
 * "Meine" is a SERVER filter (issue 27): the browser never learns the caller's group
 * memberships, so the pill sends `?mine=true` and the chip count comes from a second
 * include_total query (no business logic in the UI). Mock mode has no server and keeps
 * the local assigned_to match. Assignee/Gruppe names come from the list row (issue 20);
 * the members query survives only as the mock-mode fallback for the caller's own id.
 *
 * [Source: apps/web/src/features/invoices/InvoicesScreen.tsx (list chrome)]
 * [Source: apps/web/src/features/communication-v2/EingangScreen.tsx (task-backed T5 + decisions)]
 * [Source: apps/web/src/features/invoices/task-link.ts (deep-link contract)]
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, LayoutGrid, List, ListPlus, X, XCircle } from 'lucide-react';
import { ListView } from '../../components/data/ListView';
import { DataTable } from '../../components/data/DataTable';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { Button } from '../../components/ui/Button';
import { StatusDot } from '../../components/ui/StatusDot';
import { Badge } from '../../components/ui/Badge';
import { EntityLink } from '../../components/ui/EntityLink';
import { useAdminMembers } from '../administration/hooks';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { loadBrokerSession } from '../../lib/auth/auth-session';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';
import { isLiveData } from '../../lib/data-mode';
import { useTaskList } from './hooks/useTaskList';
import { useTaskDecision, type TaskDecisionAction } from './hooks/useTaskDecision';
import { taskActionErrorMessage } from '../../lib/task-action-error';
import { useCreateTask } from './hooks/useCreateTask';
import {
  TASK_PRESETS,
  MEINE_PRESET_KEY,
  assigneeLabel,
  processLabel,
  statusLabel,
  STATUS_TONE,
  derivePriority,
  PRIORITY_LABEL,
  PRIORITY_VARIANT,
  faellig,
  sourceLabel,
} from './api/aufgaben-api';
import { resolveTaskRoute } from './task-routes';
import { AufgabePeek } from './components/AufgabePeek';
import { AufgabenBoard } from './components/AufgabenBoard';
import { NeueAufgabeDialog } from './components/NeueAufgabeDialog';
import type { TaskRow } from './types';

/** A task in a terminal state has no pending decision (its primary action = reopen). */
function isTerminal(task: TaskRow): boolean {
  return task.status === 'done' || task.status === 'cancelled';
}

/** Task statuses offered in the Filter panel (statusLabel supplies the German label). */
const STATUS_FILTER_OPTIONS: TaskRow['status'][] = [
  'open',
  'in_progress',
  'awaiting_approval',
  'done',
  'cancelled',
];

/**
 * Column factory: takes the Kunde-navigation callback so the Kunde cell is a REAL
 * entity link (app-wide EntityLink convention). The Quelle cell's second line is
 * the assignee/Gruppe name the server already resolved on the row (issue 20).
 */
function makeColumns(
  onOpenKunde: (subjectType: string, id: string) => void,
): ColumnDef<TaskRow>[] {
  return [
    {
      accessorKey: 'title',
      header: 'Titel',
      cell: ({ row }) => (
        <div className="leading-tight">
          <div className="font-medium text-foreground">{row.original.title ?? '(ohne Titel)'}</div>
          <div className="text-xs text-muted-foreground">{processLabel(row.original.process)}</div>
        </div>
      ),
    },
    {
      accessorKey: 'kunde_name',
      header: 'Kunde / Police',
      cell: ({ row }) => {
        const t = row.original;
        const kunde = t.kunde_name;
        const canLink = kunde && t.subject_id && (t.subject_type === 'person' || t.subject_type === 'firma');
        return (
          <div className="leading-tight">
            <div>
              {canLink ? (
                <EntityLink onActivate={() => onOpenKunde(t.subject_type!, t.subject_id!)}>
                  {kunde}
                </EntityLink>
              ) : (
                <span className="text-foreground">{kunde ?? '-'}</span>
              )}
            </div>
            {t.subject && <div className="text-xs text-muted-foreground">{t.subject}</div>}
          </div>
        );
      },
    },
    {
      accessorKey: 'due_at',
      header: 'Fälligkeit',
      sortingFn: (a, b) => {
        const av = a.original.due_at ? Date.parse(a.original.due_at) : Infinity;
        const bv = b.original.due_at ? Date.parse(b.original.due_at) : Infinity;
        return av - bv;
      },
      cell: ({ row }) => {
        const f = faellig(row.original.due_at);
        return <StatusDot tone={f.tone} label={f.text} />;
      },
    },
    {
      id: 'prioritaet',
      header: 'Priorität',
      cell: ({ row }) => {
        const p = derivePriority(row.original);
        return <Badge variant={PRIORITY_VARIANT[p]}>{PRIORITY_LABEL[p]}</Badge>;
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <StatusDot tone={STATUS_TONE[row.original.status]} label={statusLabel(row.original.status)} />
      ),
    },
    {
      id: 'quelle',
      header: 'Quelle',
      cell: ({ row }) => (
        <div className="leading-tight">
          <div className="text-sm text-muted-foreground">{sourceLabel(row.original)}</div>
          <div className="text-xs text-muted-foreground/80">{assigneeLabel(row.original)}</div>
        </div>
      ),
    },
  ];
}

export function AufgabenScreen() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const live = isLiveData();

  // Search is a SERVER param in live mode (forwarded + keyed); it is also applied
  // client-side below so mock mode filters too (staleTime:Infinity, no refetch).
  const [search, setSearch] = useState('');
  const [activeView, setActiveView] = useState(0);
  // Advanced filters — the ListView "Filter" panel narrows by task status + process
  // (client-side over the loaded rows, alongside the preset + search).
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [processFilter, setProcessFilter] = useState<string | null>(null);

  // Issue 27: "Meine" is the one preset with a SERVER predicate. The client cannot
  // see the caller's Funktionsgruppen, so it sends ?mine=true and renders the answer
  // instead of guessing (coding-standards §3: no business logic in the UI). Mock mode
  // has no server and falls back to the local assigned_to match below.
  const preset = TASK_PRESETS[activeView] ?? TASK_PRESETS[0]!;
  const mineOnServer = live && preset.key === MEINE_PRESET_KEY;

  const { data, isLoading, isError } = useTaskList({
    ...(search.trim() ? { q: search.trim() } : {}),
    ...(mineOnServer ? { mine: true } : {}),
  });
  const rows = data?.rows ?? [];
  const total = data?.total ?? null;

  // The "Meine" chip count for the same reason: it is the server total, not a count
  // over the loaded page. limit:1 keeps it to the total_count the api already computes.
  const mineCount = useTaskList({ mine: true, include_total: true, limit: 1 });

  // strict:false, not aufgabenRoute.useSearch(): the screen is also mounted under
  // test-local routers that do not carry the app's route object.
  const { task: deepLinkTaskId } = useSearch({ strict: false }) as { task?: string };

  const [viewMode, setViewMode] = useState<'list' | 'board'>('list');
  // Seeded from ?task= so the Bearbeitungsmaske can open one Aufgabe's peek directly.
  const [selectedId, setSelectedId] = useState<string | null>(deepLinkTaskId ?? null);
  const [bulkIds, setBulkIds] = useState<Set<string>>(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Transient feedback (mirrors EingangScreen): success banner + error toast.
  const [feedback, setFeedback] = useState<string | null>(null);
  const [errorFeedback, setErrorFeedback] = useState<string | null>(null);
  function showFeedback(msg: string) {
    setFeedback(msg);
    setTimeout(() => setFeedback(null), 2500);
  }
  function showError(msg: string) {
    setErrorFeedback(msg);
    setTimeout(() => setErrorFeedback(null), 4000);
  }

  const decide = useTaskDecision();
  const create = useCreateTask();

  // ── "Neue Aufgabe" gate ──
  // The tasks surface is open-by-default (JWT-only — every sibling task action
  // carries NO @RequirePermission, and neither does POST /tasks); the api guard
  // remains the hard line. So the affordance is shown to any authenticated caller
  // (permissions resolved). useEffectivePermissions is wired for the pattern +
  // to defer the button until the caller context is known.
  const perms = useEffectivePermissions();
  const canCreate = !perms.isLoading && !perms.isError;

  // ── Caller resolution: GET /me/profile app_user id (every authenticated user) ──
  // The caller's own id comes from GET /me/profile (JWT-only, visibility.read_own, NOT the
  // admin-gated GET /admin/members / role.read). This is the id task.assigned_to references.
  // Since issue 27 the live "Meine" filter no longer needs it (the server resolves the
  // caller itself); it still backs the mock-mode preset + count. Shares the AppShell /
  // settings query key so the profile is fetched once. Disabled in mock mode (network-free).
  const { data: meProfile } = useQuery({
    queryKey: qk.meProfile(),
    queryFn: () => apiClient.me.profile(),
    enabled: live,
    staleTime: 60_000,
  });

  // Members list is loaded ONLY as the mock-mode fallback for the caller's own id
  // (GET /me/profile is live-only). Assignee NAMES no longer come from here: they are
  // on the list row itself (issue 20), so a non-admin sees them too.
  const { data: members } = useAdminMembers();
  const myUserId = useMemo(() => {
    if (meProfile?.id) return meProfile.id;
    const email = loadBrokerSession()?.email?.toLowerCase();
    if (!email || !members) return null;
    return members.members.find((m) => m.email.toLowerCase() === email)?.user_id ?? null;
  }, [meProfile, members]);

  // Kunde navigation — shared by the table cell (makeColumns) and the board card
  // so both surfaces link a Kunde identically (person/firma → /kunden/$id).
  const openKunde = useCallback(
    (subjectType: string, id: string) =>
      void navigate(
        subjectType === 'firma' || subjectType === 'person'
          ? { to: '/kunden/$id', params: { id } }
          : { to: '/aufgaben' },
      ),
    [navigate],
  );

  const columns = useMemo(() => makeColumns(openKunde), [openKunde]);

  // Preset (client-side over loaded rows) + search (client-side; also server-side
  // in live) + default Fälligkeit-asc sort (soonest/overdue first).
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      // Live "Meine" was already applied by the server; re-applying the local
      // predicate would drop exactly the group-assigned rows issue 27 is about.
      if (!mineOnServer && !preset.match(r, myUserId)) return false;
      if (statusFilter && r.status !== statusFilter) return false;
      if (processFilter && r.process !== processFilter) return false;
      if (!q) return true;
      return `${r.title ?? ''} ${r.kunde_name ?? ''} ${r.subject ?? ''} ${r.from ?? ''}`
        .toLowerCase()
        .includes(q);
    });
    return [...filtered].sort((a, b) => {
      const av = a.due_at ? Date.parse(a.due_at) : Infinity;
      const bv = b.due_at ? Date.parse(b.due_at) : Infinity;
      return av - bv;
    });
  }, [rows, preset, mineOnServer, search, myUserId, statusFilter, processFilter]);

  // Chip counts stay client-side over the loaded page for every pure predicate;
  // "Meine" is the server total (the client cannot recompute it, see above).
  const views = TASK_PRESETS.map((p) => ({
    label: p.label,
    count:
      live && p.key === MEINE_PRESET_KEY
        ? (mineCount.data?.total ?? 0)
        : rows.filter((r) => p.match(r, myUserId)).length,
  }));

  const selectedIndex = visible.findIndex((r) => r.id === selectedId);
  const selected = selectedIndex >= 0 ? visible[selectedIndex]! : null;

  /** Row activation: deep-link routable processes; else open the decision peek. */
  function openRow(task: TaskRow) {
    const target = resolveTaskRoute(task);
    if (!target) {
      setSelectedId(task.id);
      return;
    }
    if (target.to === '/rechnungen') void navigate(target);
    else void navigate({ to: target.to });
  }

  // ── Selection (bulk) ──
  function toggleRow(id: string) {
    setBulkIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleAll(next: boolean) {
    setBulkIds(next ? new Set(visible.map((r) => r.id)) : new Set());
  }
  function clearSelection() {
    setBulkIds(new Set());
  }

  const BULK_LABEL: Record<'complete' | 'cancel', string> = {
    complete: 'erledigt',
    cancel: 'abgebrochen',
  };

  /** Apply one bulk action across the selected ids (POST /tasks/bulk). */
  async function runBulk(action: 'complete' | 'cancel') {
    const ids = [...bulkIds];
    if (ids.length === 0) return;
    if (live) {
      try {
        const res = await apiClient.tasks.bulk(ids, action);
        const ok = res.results.filter((r) => r.ok).length;
        const failed = res.results.length - ok;
        await qc.invalidateQueries({ queryKey: ['tasks', 'list'] });
        showFeedback(
          failed > 0
            ? `${ok} ${BULK_LABEL[action]}, ${failed} fehlgeschlagen`
            : `${ok} Aufgabe${ok === 1 ? '' : 'n'} ${BULK_LABEL[action]}`,
        );
      } catch {
        showError('Sammelaktion fehlgeschlagen.');
        return;
      }
    } else {
      showFeedback(`${ids.length} Aufgabe${ids.length === 1 ? '' : 'n'} ${BULK_LABEL[action]}`);
    }
    clearSelection();
  }

  // ── Single-task decision (peek DecisionBar) with Auto-Next ──
  const DECISION_LABEL: Record<TaskDecisionAction, string> = {
    complete: 'Aufgabe erledigt',
    inProgress: 'In Arbeit genommen',
    cancel: 'Aufgabe abgebrochen',
    reopen: 'Aufgabe wieder geöffnet',
  };

  async function runAction(task: TaskRow, action: TaskDecisionAction) {
    // A terminal decision (done / cancelled / reopened) advances to the next task
    // (Auto-Next); "In Arbeit" keeps the current task in view. Capture the next id
    // BEFORE the mutation — a live invalidation may reorder/drop the row.
    const advance = action !== 'inProgress';
    const nextId =
      selectedIndex >= 0 && selectedIndex < visible.length - 1 ? visible[selectedIndex + 1]!.id : null;
    setBusyId(task.id);
    try {
      await decide.mutateAsync({ id: task.id, action });
      showFeedback(DECISION_LABEL[action]);
      if (advance) setSelectedId(nextId); // null → closes the peek (last task)
    } catch (err) {
      // The api says WHY it refused (a staged task or one carrying a held action closes
      // with the Gesamtfreigabe, not here). Showing our own sentence hid that.
      showError(taskActionErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  }

  // Primary action (E/Enter): reopen a terminal task, else complete it. Held in a
  // ref so the keyboard effect stays subscribed once (reads the latest closure).
  const primaryActionRef = useRef<() => void>(() => {});
  primaryActionRef.current = () => {
    if (!selected) return;
    void runAction(selected, isTerminal(selected) ? 'reopen' : 'complete');
  };

  // Keyboard: J/K navigate the peek, E/Enter = primary action, Esc closes
  // (suppressed while typing in an input/textarea/select).
  useEffect(() => {
    if (selectedId === null) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'Escape') setSelectedId(null);
      else if ((e.key === 'j' || e.key === 'J') && selectedIndex >= 0 && selectedIndex < visible.length - 1)
        setSelectedId(visible[selectedIndex + 1]!.id);
      else if ((e.key === 'k' || e.key === 'K') && selectedIndex > 0)
        setSelectedId(visible[selectedIndex - 1]!.id);
      else if (e.key === 'e' || e.key === 'E' || e.key === 'Enter') {
        e.preventDefault();
        primaryActionRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, selectedIndex, visible]);

  // Filter panel (the ListView "Filter" button): Status + Prozess. Prozess options are the
  // distinct processes actually present in the loaded rows (there are many process types).
  const processOptions = useMemo(
    () => [...new Set(rows.map((r) => r.process))].sort(),
    [rows],
  );
  const activeFilterCount = (statusFilter ? 1 : 0) + (processFilter ? 1 : 0);
  const filterPanel = (
    <div className="flex flex-wrap items-end gap-3">
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        Status
        <select
          value={statusFilter ?? ''}
          onChange={(e) => setStatusFilter(e.target.value || null)}
          className="h-9 rounded-md border border-border bg-background px-2 text-sm text-foreground"
        >
          <option value="">Alle</option>
          {STATUS_FILTER_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {statusLabel(s)}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        Prozess
        <select
          value={processFilter ?? ''}
          onChange={(e) => setProcessFilter(e.target.value || null)}
          className="h-9 rounded-md border border-border bg-background px-2 text-sm text-foreground"
        >
          <option value="">Alle</option>
          {processOptions.map((p) => (
            <option key={p} value={p}>
              {processLabel(p)}
            </option>
          ))}
        </select>
      </label>
      {activeFilterCount > 0 && (
        <button
          type="button"
          onClick={() => {
            setStatusFilter(null);
            setProcessFilter(null);
          }}
          className="h-9 rounded-md px-2.5 text-sm text-muted-foreground hover:text-foreground"
        >
          Zurücksetzen
        </button>
      )}
    </div>
  );

  return (
    <>
      <ListView
        title="Aufgaben"
        views={views}
        activeView={activeView}
        onViewChange={setActiveView}
        filterPanel={filterPanel}
        activeFilterCount={activeFilterCount}
        searchValue={search}
        onSearchChange={setSearch}
        count={`${visible.length} von ${total ?? rows.length}`}
        searchPlaceholder="Titel, Kunde, Betreff …"
        rightAction={
          <div className="flex items-center gap-2">
            <SegmentedControl
              ariaLabel="Ansicht"
              value={viewMode}
              onValueChange={(v) => setViewMode(v as 'list' | 'board')}
              options={[
                { value: 'list', label: 'Liste', icon: <List className="size-4" /> },
                { value: 'board', label: 'Board', icon: <LayoutGrid className="size-4" /> },
              ]}
            />
            {canCreate && (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <ListPlus className="size-4" /> Neue Aufgabe
              </Button>
            )}
          </div>
        }
      >
        {/* Bulk action bar — appears once ≥1 row is selected. */}
        {bulkIds.size > 0 && (
          <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-secondary/50 px-3 py-2">
            <span className="text-sm font-medium text-foreground">
              {bulkIds.size} ausgewählt
            </span>
            <div className="flex-1" />
            <Button
              size="sm"
              className="bg-sem-ok text-white hover:bg-sem-ok/90"
              onClick={() => void runBulk('complete')}
            >
              <CheckCircle2 className="size-4" /> Erledigen
            </Button>
            <Button size="sm" variant="outline" onClick={() => void runBulk('cancel')}>
              <XCircle className="size-4" /> Abbrechen
            </Button>
            <Button size="sm" variant="ghost" onClick={clearSelection}>
              <X className="size-4" /> Auswahl aufheben
            </Button>
          </div>
        )}

        {isLoading ? (
          <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
            Lade Aufgaben …
          </div>
        ) : isError ? (
          <div role="alert" className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
            Aufgaben konnten nicht geladen werden.
          </div>
        ) : visible.length === 0 ? (
          <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
            Keine Aufgaben in diesem Filter.
          </div>
        ) : viewMode === 'board' ? (
          <AufgabenBoard rows={visible} onOpenTask={openRow} onOpenKunde={openKunde} />
        ) : (
          <DataTable
            columns={columns}
            data={visible}
            onRowClick={openRow}
            selectable
            getRowId={(r) => r.id}
            selectedIds={bulkIds}
            onToggleRow={toggleRow}
            onToggleAll={toggleAll}
          />
        )}
      </ListView>

      {selected && (
        <AufgabePeek
          key={selected.id}
          task={selected}
          index={selectedIndex}
          total={visible.length}
          busy={busyId === selected.id}
          onClose={() => setSelectedId(null)}
          onPrev={() => {
            if (selectedIndex > 0) setSelectedId(visible[selectedIndex - 1]!.id);
          }}
          onNext={() => {
            if (selectedIndex >= 0 && selectedIndex < visible.length - 1)
              setSelectedId(visible[selectedIndex + 1]!.id);
          }}
          onComplete={() => void runAction(selected, 'complete')}
          onInProgress={() => void runAction(selected, 'inProgress')}
          onCancel={() => void runAction(selected, 'cancel')}
          onReopen={() => void runAction(selected, 'reopen')}
        />
      )}

      <NeueAufgabeDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        members={members?.members ?? []}
        onCreate={(payload) => create.mutateAsync(payload)}
        onCreated={() => showFeedback('Aufgabe erstellt')}
      />

      {/* Success banner (aria-live) */}
      {feedback && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-lg border border-sem-ok/40 bg-sem-ok-bg px-4 py-2.5 text-sm text-sem-ok shadow-md"
        >
          {feedback}
        </div>
      )}

      {/* Error toast */}
      {errorFeedback && (
        <div
          role="alert"
          className="fixed bottom-16 right-4 z-50 flex items-center gap-2 rounded-lg border border-sem-alert/40 bg-sem-alert-bg px-4 py-2.5 text-sm text-sem-alert shadow-md"
        >
          {errorFeedback}
        </div>
      )}
    </>
  );
}
